import { CalendarDays, Mic } from 'lucide-react';
import type { MemoryField } from '../../shared/memory.ts';
import { useMemory } from '../shell/useMemory.tsx';

const DAY = 86_400_000;

// Whole month words only: "may" must not match "maybe", nor "dec" "decisions".
const MONTH =
  '(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\\b\\.?';
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const PAST_WINDOW = 60; // days: a year-less date this recent is "just passed", not next year's

/**
 * Days until a free-text date ("15 October", "Oct 15th, 2026", "2026-10-15"), counted in the user's local calendar;
 * negative when it passed in the last 60 days. Null for anything else ("next Friday", "31 September") or a date more
 * than a year out. Parsed by hand: Date.parse reads "next Friday 2026" as 1 January.
 */
export function daysUntil(text: string, now = new Date()): number | null {
  const t = text.toLowerCase();
  let year: number | undefined;
  let month = -1;
  let day = 0;
  const iso = t.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/);
  const dayFirst = t.match(new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH}(?:,?\\s+(\\d{4}))?`));
  const monthFirst = t.match(new RegExp(`\\b${MONTH}\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b(?:,?\\s+(\\d{4}))?`));
  const monthOf = (word = '') => MONTHS.indexOf(word.slice(0, 3));
  if (iso) [year, month, day] = [Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])];
  else if (dayFirst) [day, month, year] = [Number(dayFirst[1]), monthOf(dayFirst[2]), Number(dayFirst[3]) || undefined];
  else if (monthFirst)
    [month, day, year] = [monthOf(monthFirst[1]), Number(monthFirst[2]), Number(monthFirst[3]) || undefined];
  if (month < 0 || month > 11 || day < 1) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const days = (y: number) => {
    const d = new Date(y, month, day);
    if (d.getMonth() !== month) return null; // 31 September rolls into October: not a date
    return Math.round((d.getTime() - today) / DAY);
  };
  // No year given: the nearest of last year, this year and next that falls in the window ("20 December" in early
  // January just passed; "5 January" in late December is coming up).
  const y = now.getFullYear();
  const inWindow = (d: number | null): d is number => d !== null && d >= -PAST_WINDOW && d <= 366;
  const candidates = year ? [days(year)] : [days(y - 1), days(y), days(y + 1)];
  const result = candidates.filter(inWindow).sort((a, b) => Math.abs(a) - Math.abs(b))[0] ?? null;
  return result !== null && result >= -PAST_WINDOW && result <= 366 ? result : null;
}

export function InterviewTab({ onPractise }: { onPractise: () => void }) {
  const { memory } = useMemory();
  const field = (k: MemoryField) => memory.fields[k]?.value || '';
  const role = field('interview.role');
  const company = field('interview.company');
  const date = field('interview.date');
  const round = field('interview.round');
  const jd = field('interview.jdSummary');
  const days = date ? daysUntil(date) : null;
  const practiced = [...memory.practiced].reverse();

  return (
    <div data-testid="interview-tab" className="mx-auto w-full max-w-2xl space-y-5 px-4 pb-28 pt-10">
      <header className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-subtle">Your interview</p>
        <h1 className="text-2xl font-semibold tracking-tight text-fg">
          {role || 'Interview'}
          {company ? <span className="text-muted"> at {company}</span> : null}
        </h1>
        {round ? <p className="text-sm text-subtle">{round}</p> : null}
      </header>

      {date ? (
        <section className="card flex items-center gap-4 ring-1 ring-fg/10" data-testid="interview-countdown">
          <CalendarDays className="size-6 shrink-0 text-accent" aria-hidden />
          <div>
            <p className="text-[15px] text-fg/85">{date}</p>
            {days !== null ? (
              <p className="text-sm text-subtle">
                {days < 0
                  ? `${-days} day${days === -1 ? '' : 's'} ago. How did it go? Tell fuse on your next call.`
                  : days === 0
                    ? 'Today. You have got this.'
                    : days === 1
                      ? 'Tomorrow'
                      : `In ${days} days`}
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      {jd ? (
        <details className="card ring-1 ring-fg/10">
          <summary className="cursor-pointer select-none text-xs font-medium uppercase tracking-wide text-subtle">
            Job description
          </summary>
          <p className="mt-3 text-[15px] text-muted">{jd}</p>
        </details>
      ) : null}

      <section className="card space-y-3 ring-1 ring-fg/10" data-testid="interview-practiced">
        <h2 className="text-xs font-medium uppercase tracking-wide text-subtle">Questions you have practised</h2>
        {practiced.length ? (
          <ul className="space-y-2.5">
            {practiced.map((p) => (
              <li key={`${p.at}-${p.question}`} className="flex items-start gap-3">
                <span className="flex-1 text-[15px] text-fg/85">{p.question}</span>
                {p.overall !== null ? (
                  <span className="shrink-0 text-sm text-subtle">
                    {p.overall}
                    {p.cefr ? (
                      <span className="ml-1.5 rounded-full bg-accent/10 px-2 py-0.5 text-xs text-accent">{p.cefr}</span>
                    ) : null}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-subtle">None yet.</p>
        )}
      </section>

      <button type="button" data-testid="interview-practise" onClick={onPractise} className="btn-primary">
        <Mic className="size-4" /> Practise a question on a call
      </button>
    </div>
  );
}
